import { useCallback, useEffect, useMemo } from "react";
import type { FilterWidget as FilterWidgetType } from "../../../../../api/types/dashboardsTypes";
import { FilterComponentType, ReferenceDateType } from "../../../../../api/types/dashboardsTypes";
import type { ConditionDescriptor } from "../../../../../api/types/fieldConfigurationTypes";
import SkeletonLoader from "../../components/SkeletonLoader";
import { useDashboardContext } from "../../contexts/DashboardContext";
import type { DashboardFilter } from "../../dashboard.types";
import WidgetNotConfigured from "../smartview/WidgetNotConfigured";
import FilterDropdown from "./FilterDropdown";
import FilterWidgetBusyOverlay from "./FilterWidgetBusyOverlay";
import { getFilterWidgetValues } from "./getFilterWidgetValues";
import { getParentCondition } from "./parentNarrowing";
import ReferenceDatePicker from "./settings/ReferenceDatePicker";
import TextList from "./TextList";
import TextTiles from "./TextTiles";

interface Props {
  widget: FilterWidgetType;
}

const FilterWidget = ({ widget }: Props) => {
  const { dashboardFilterId } = widget;
  const {
    filters,
    dashboardLoaded,
    updateWidget,
    setActiveFilter,
    removeActiveFilter,
    setSelectedWidgetId,
    updateTile,
    revalidateChildFilters,
  } = useDashboardContext();
  const filter = filters.find((f) => f.id === dashboardFilterId);

  const handleOnChange = useCallback(
    (settings: Partial<FilterWidgetType>) => {
      updateWidget(widget.widgetId, settings);
      if (filter !== undefined && (settings.defaultValue !== undefined || settings.referenceDate !== undefined)) {
        void revalidateChildFilters(widget.widgetId, getFilterWidgetValues(filter, { ...widget, ...settings }));
      }
    },
    [filter, revalidateChildFilters, updateWidget, widget]
  );

  useEffect(() => {
    if (!filter) {
      removeActiveFilter(dashboardFilterId, widget.widgetId);
      return;
    }
    const activeFilter = {
      id: filter.id,
      widgetId: widget.widgetId,
      values: getFilterWidgetValues(filter, widget),
      applyToWidgets: [widget.widgetId],
    };
    setActiveFilter(activeFilter);
  }, [dashboardFilterId, filter, removeActiveFilter, setActiveFilter, widget, widget.widgetId]);

  useEffect(() => {
    updateTile({ id: widget.widgetId, isLoaded: true });
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  const handleOnConfigure = useCallback(() => {
    setSelectedWidgetId(widget.widgetId);
  }, [setSelectedWidgetId, widget.widgetId]);

  if (!dashboardLoaded && !filter) {
    return <SkeletonLoader />;
  }

  if (!filter) {
    return <WidgetNotConfigured onConfigure={handleOnConfigure} />;
  }

  if (filter.dataType === "date") {
    return <FilterWidgetDate widget={widget} onChange={handleOnChange} />;
  }

  return <FilterWidgetText widget={widget} filter={filter} onChange={handleOnChange} />;
};

const FilterWidgetDate = ({
  widget,
  onChange,
}: {
  widget: FilterWidgetType;
  onChange: (settings: Partial<FilterWidgetType>) => void;
}) => {
  const { defaultValue, referenceDate } = widget;

  const handleOnChange = (valueType: ReferenceDateType, customValue: string[]) => {
    onChange({
      referenceDate: valueType,
      defaultValue: customValue,
    });
  };

  return (
    <ReferenceDatePicker
      valueType={referenceDate || ReferenceDateType.ThisMonth}
      customValue={defaultValue as string[]}
      onChange={handleOnChange}
    />
  );
};

const FilterWidgetText = ({
  widget,
  filter,
  onChange,
}: {
  widget: FilterWidgetType;
  filter: DashboardFilter;
  onChange: (settings: Partial<FilterWidgetType>) => void;
}) => {
  const { filterWidgets, activeFilters, revalidatingWidgetIds } = useDashboardContext();

  const parentCondition = useMemo(
    () => getParentCondition(filterWidgets, activeFilters, widget.widgetId),
    [filterWidgets, activeFilters, widget.widgetId]
  );
  const isRevalidating = revalidatingWidgetIds.includes(widget.widgetId);

  return (
    <FilterWidgetBusyOverlay busy={isRevalidating}>
      <FilterWidgetBody widget={widget} filter={filter} parentCondition={parentCondition} onChange={onChange} />
    </FilterWidgetBusyOverlay>
  );
};

const FilterWidgetBody = ({
  widget,
  filter,
  parentCondition,
  onChange,
}: {
  widget: FilterWidgetType;
  filter: DashboardFilter;
  parentCondition?: ConditionDescriptor;
  onChange: (settings: Partial<FilterWidgetType>) => void;
}) => {
  if (widget.componentType === FilterComponentType.List) {
    return <TextList filter={filter} settings={widget} parentCondition={parentCondition} onSettingsChange={onChange} />;
  }

  if (widget.componentType === FilterComponentType.Tile) {
    return <TextTiles filter={filter} settings={widget} onSettingsChange={onChange} />;
  }

  return (
    <FilterDropdown filter={filter} settings={widget} parentCondition={parentCondition} onSettingsChange={onChange} />
  );
};

export default FilterWidget;
